import * as React from 'react';
import { styled } from '@glitz/react';
import { useDynamicData } from '@avensia/excite';
import { useFreezeScroll } from '@avensia/excite-ui';
import { ZIndex } from 'webapp/z-index';
import { useShopTheLookDialogContext } from './ShopTheLookContext';
import Image from 'webapp/components/image';
import { CloseIcon } from 'webapp/components/icons';
import { Button } from 'webapp/components/button';
import { Theme } from 'webapp/theme';
import { createMaxSmallViewportDecorator } from 'webapp/viewport';
import { scrollbarDecorator } from 'webapp/pages/ProductPage/scrollbar';
import { white } from 'webapp/shared/sharedTheme';
import ShopTheLookProductCard from './ShopTheLookProductCard';

export default function ShopTheLookDialog() {
  const { isOpen, setIsOpen, selectedItem, title } = useShopTheLookDialogContext();
  const [productsData] = useDynamicData<NordicNest.Api.ProductsBySkusApiModel>({ context: selectedItem?.skus });
  const [zoomedIn, setZoomedIn] = React.useState(false);
  const [horizontallyZoomScroll, setHorizontallyZoomScroll] = React.useState(false);
  const imageSectionRef = React.useRef<HTMLDivElement>(null);
  useFreezeScroll(isOpen);

  const handleClose = React.useCallback(() => {
    setIsOpen(false);
    setZoomedIn(false);
    setHorizontallyZoomScroll(false);
  }, [setIsOpen]);

  const handleImageClick = React.useCallback(() => {
    if (zoomedIn) {
      setZoomedIn(false);
      return;
    }

    if (!imageSectionRef.current) {
      return;
    }

    const imageElement = imageSectionRef.current.querySelector('img');
    if (!imageElement) {
      return;
    }

    const imageSectionWidth = imageSectionRef.current.offsetWidth;
    const imageWidth = imageElement.offsetWidth;

    if (imageWidth === imageSectionWidth) {
      setHorizontallyZoomScroll(true);
    }
    setZoomedIn(!zoomedIn);
  }, [zoomedIn]);

  if (!selectedItem) {
    return null;
  }

  return isOpen ? (
    <DialogContent>
      <ImageSection ref={imageSectionRef} css={zoomedIn && imageSectionZoomedDecorator}>
        <StyledImage
          image={{
            url: selectedItem.images[0].url,
            alt: selectedItem.images[0].altText,
            width: selectedItem.images[0].width || 2000,
            height: selectedItem.images[0].height || 2000,
          }}
          css={zoomedIn && (horizontallyZoomScroll ? horizontallyZoomedDecorator : zoomedInDecorator)}
          onClick={handleImageClick}
        />
      </ImageSection>
      <ProductsSection>
        <Header>
          <ProductsHeading>{title}</ProductsHeading>
          <CloseButton disableRippleEffect onClick={handleClose} as={Theme.Transparent} square>
            <CloseIcon />
          </CloseButton>
        </Header>
        <ProductsGrid>
          {productsData?.products?.map(product => <ShopTheLookProductCard key={product.sku} {...product} />)}
        </ProductsGrid>
      </ProductsSection>
    </DialogContent>
  ) : null;
}

const DialogContent = styled.div({
  position: 'fixed',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  display: 'flex',
  backgroundColor: white,
  zIndex: ZIndex.Overlay,
});

const ImageSection = styled.div(
  scrollbarDecorator,
  createMaxSmallViewportDecorator({
    display: 'none',
  }),
  {
    overflow: 'hidden',
    display: 'flex',
    flexGrow: 1,
  },
);

const StyledImage = styled(Image, {
  width: 'auto',
  height: '100%',
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
  cursor: 'zoom-in',
  margin: { xy: 'auto' },
});

const zoomedInDecorator = styled({
  width: '100%',
  height: 'auto',
  maxHeight: 'auto',
  cursor: 'zoom-out',
});

const horizontallyZoomedDecorator = styled({
  width: 'auto',
  height: '100%',
  maxWidth: 'auto',
  cursor: 'zoom-out',
});

const imageSectionZoomedDecorator = styled(scrollbarDecorator, {
  display: 'initial',
  overflow: 'auto',
});

const ProductsSection = styled.div(
  createMaxSmallViewportDecorator({
    width: '100%',
  }),
  {
    display: 'flex',
    flexDirection: 'column',
    width: 390,
    flexShrink: 0,
  },
);

const Header = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
  padding: { left: t => t.sharedSpacing.m, right: t => t.sharedSpacing.xs },
  height: 64,
});

const ProductsHeading = styled.h2({
  fontSize: t => t.sharedFontSize.m,
  marginBottom: 0,
});

const ProductsGrid = styled.div(scrollbarDecorator, {
  display: 'flex',
  flexDirection: 'column',
  gap: t => t.sharedSpacing.xs,
  padding: { xy: t => t.sharedSpacing.m },
  overflowY: 'auto',
  overflowX: 'hidden',
});

const CloseButton = styled(Button, {
  padding: { xy: t => t.sharedSpacing.xs },
});
